"use client";

import type React from "react";

import { useState, useRef, useEffect, memo } from "react";
import {
  Card,
  Box,
  Typography,
  Chip,
  TextField,
  IconButton,
  InputAdornment,
  Tooltip,
  Collapse,
  LinearProgress,
  Stack,
} from "@mui/material";
import {
  Edit as EditIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  ExpandMore,
  ExpandLess,
  Event as EventIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Archive as ArchiveIcon,
} from "@mui/icons-material";
import type { Segment, SegmentTaskInfo } from "./lib/types";
import type { DragData } from "./lib/hooks/use-drag-drop";
import {
  formatHours,
  getBorderLeftColor,
  parseHoursFromHMM,
} from "./lib/utils/schedular-utils";
import {
  formatTaskWorkDate,
  getSegmentDragHours,
  getSegmentStatusVisual,
  getSegmentTaskSummary,
  getTaskStatusLabel,
  getTaskStatusVisual,
  isSegmentDraggable,
  isTaskDraggable,
  normalizeSegmentSchedulingStatus,
} from "./lib/utils/segment-scheduling-utils";
import { useLoading } from "../common/context/LoadingContext";
import { WorkOrderQueueAPI } from "../QueueBoard/api/workOrderApi";
import { ConcurrencyError } from "../common/Utils/concurrencyError";
import { useSnackbar } from "notistack";

const SEGMENT_EDIT_CACHE_KEY = "segment_concurrency_cache";

const cacheSegmentEdit = (segmentId: string, hours: number) => {
  try {
    localStorage.setItem(
      SEGMENT_EDIT_CACHE_KEY,
      JSON.stringify({ segmentId, hours, timestamp: Date.now() }),
    );
  } catch (e) {
    console.error("Failed to cache segment edit:", e);
  }
};

export const getCachedSegmentEdit = (): {
  segmentId: string;
  hours: number;
} | null => {
  try {
    const cached = localStorage.getItem(SEGMENT_EDIT_CACHE_KEY);
    if (cached) {
      const cacheData = JSON.parse(cached);
      if (Date.now() - cacheData.timestamp < 5 * 60 * 1000) {
        return { segmentId: cacheData.segmentId, hours: cacheData.hours };
      }
    }
  } catch (e) {
    console.error("Failed to get cached segment edit:", e);
  }
  return null;
};

export const clearCachedSegmentEdit = () => {
  try {
    localStorage.removeItem(SEGMENT_EDIT_CACHE_KEY);
  } catch (e) {
    console.error("Failed to clear cached segment edit:", e);
  }
};

interface SegmentItemProps {
  segment: Segment;
  workOrderName: string;
  workOrderPriority: "high" | "medium" | "low";
  woStatusId?: number;
  reserved: number;
  isBroken: boolean;
  onDragStart: (e: React.DragEvent, data: DragData) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onUpdateHours?: (segmentId: string, newHours: number) => void;
  onClick?: () => void;
  onConcurrencyError?: (message: string) => void;
  workOrderNumber?: string;
  invoiceNumber?: string;
  customerName?: string;
  customerNumber?: string;
  unitNumber?: string;
  unitModel?: string;
  vinNumber?: string;
  myReference?: string;
  woDescription?: string;
  woPriorityId?: number;
}

function SegmentTaskRow({
  task,
  segment,
  onDragStart,
  onDragEnd,
  workOrderNumber,
  invoiceNumber,
  customerName,
  customerNumber,
  woStatusId,
  woDescription,
  woPriorityId,
}: {
  task: SegmentTaskInfo;
  segment: Segment;
  onDragStart: (e: React.DragEvent, data: DragData) => void;
  onDragEnd: (e: React.DragEvent) => void;
  workOrderNumber?: string;
  invoiceNumber?: string;
  customerName?: string;
  customerNumber?: string;
  woStatusId?: number;
  woDescription?: string;
  woPriorityId?: number;
}) {
  const draggable = isTaskDraggable(task);
  const visual = getTaskStatusVisual(task.taskSchedulingStatus);

  const handleDragStart = (e: React.DragEvent) => {
    if (!draggable || !task.scheduleItemId) {
      e.preventDefault();
      return;
    }
    const localId = `scheduled-${task.scheduleItemId}`;
    onDragStart(e, {
      type: "scheduled-item",
      id: localId,
      scheduledItemId: localId,
      hours: task.estimatedHours,
      workOrderId: segment.workOrderId,
      segmentId: segment.id,
      segmentName: segment.name,
      description: segment.name,
      isPastDueReschedule: true,
      workOrderNumber,
      invoiceNumber,
      customerName,
      customerNumber,
      woStatusId,
      woDescription,
      woPriorityId,
    });
  };

  return (
    <Box
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        py: 0.75,
        px: 1,
        borderRadius: 1,
        borderLeft: `3px solid ${visual.borderColor}`,
        bgcolor: visual.backgroundColor,
        opacity: visual.opacity,
        cursor: draggable ? "grab" : "not-allowed",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
          {task.technicianName || "Unassigned"}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {formatTaskWorkDate(task.workDate)} • {formatHours(task.estimatedHours)}h
        </Typography>
      </Box>
      <Chip
        size="small"
        label={getTaskStatusLabel(task.taskSchedulingStatus)}
        color={visual.chipColor}
        variant="outlined"
        sx={{ height: 22, fontSize: "0.65rem" }}
      />
    </Box>
  );
}

function SegmentItemComponent({
  segment,
  workOrderPriority,
  woStatusId,
  isBroken,
  onDragStart,
  onDragEnd,
  onUpdateHours,
  onClick,
  onConcurrencyError,
  workOrderNumber,
  invoiceNumber,
  customerName,
  customerNumber,
  unitNumber,
  unitModel,
  vinNumber,
  myReference,
  woDescription,
  woPriorityId,
}: SegmentItemProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editHours, setEditHours] = useState(segment.estimatedHours);
  const [editDisplayString, setEditDisplayString] = useState(() =>
    formatHours(segment.estimatedHours),
  );
  const [isHoveringHours, setIsHoveringHours] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { showLoading, hideLoading } = useLoading();
  const { enqueueSnackbar } = useSnackbar();

  const schedulingStatus = normalizeSegmentSchedulingStatus(
    segment.schedulingStatus,
  );
  const statusVisual = getSegmentStatusVisual(schedulingStatus);
  const canDrag = isSegmentDraggable(segment) && !isBroken;
  const dragHours = getSegmentDragHours(segment);
  const tasks = segment.tasks ?? [];
  const showTaskList = tasks.length > 0;
  const scheduledProgress =
    segment.estimatedHours > 0
      ? Math.min(
          100,
          ((segment.scheduledHours ?? 0) / segment.estimatedHours) * 100,
        )
      : 0;

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) {
      setEditHours(segment.estimatedHours);
      setEditDisplayString(formatHours(segment.estimatedHours));
    }
  }, [segment.estimatedHours, isEditing]);

  const handleDragStart = (e: React.DragEvent) => {
    if (!canDrag || dragHours <= 0) {
      e.preventDefault();
      return;
    }
    setIsDragging(true);

    // Past due: reschedule existing task(s) — do not create a new schedule entry.
    if (schedulingStatus === "past_scheduled") {
      const pastTasks = tasks.filter(
        (task) =>
          task.taskSchedulingStatus === "past_scheduled" && task.scheduleItemId,
      );
      if (pastTasks.length === 0) {
        e.preventDefault();
        setIsDragging(false);
        return;
      }
      const scheduledItemIds = pastTasks.map(
        (task) => `scheduled-${task.scheduleItemId}`,
      );
      onDragStart(e, {
        type: "scheduled-item",
        id: scheduledItemIds[0],
        scheduledItemId: scheduledItemIds[0],
        scheduledItemIds,
        hours: pastTasks.reduce((sum, task) => sum + (task.estimatedHours || 0), 0),
        workOrderId: segment.workOrderId,
        segmentId: segment.id,
        segmentName: segment.name,
        description: segment.name,
        isPastDueReschedule: true,
        workOrderNumber,
        invoiceNumber,
        customerName,
        customerNumber,
        unitNumber,
        unitModel,
        vinNumber,
        myReference,
        woStatusId,
        woDescription,
        woPriorityId,
      });
      return;
    }

    onDragStart(e, {
      type: "segment",
      id: segment.id,
      hours: dragHours,
      workOrderId: segment.workOrderId,
      segmentName: segment.name,
      description: segment.name,
      workOrderNumber,
      invoiceNumber,
      customerName,
      customerNumber,
      unitNumber,
      unitModel,
      vinNumber,
      myReference,
      woStatusId,
      woDescription,
      woPriorityId,
    });
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setIsDragging(false);
    onDragEnd(e);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (isEditing || (e.target as HTMLElement).closest(".MuiTextField-root")) {
      return;
    }
    if (!isBroken && onClick && schedulingStatus === "unscheduled") {
      onClick();
    }
  };

  const handleStartEditing = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (schedulingStatus !== "unscheduled" && schedulingStatus !== "partially_scheduled") {
      return;
    }
    setIsEditing(true);
    setEditHours(segment.estimatedHours);
    setEditDisplayString(formatHours(segment.estimatedHours));
  };

  const handleSaveHours = async () => {
    const parsedHours = parseHoursFromHMM(editDisplayString);
    const hoursToSave = parsedHours ?? editHours;
    if (hoursToSave > 0 && hoursToSave !== segment.estimatedHours) {
      try {
        showLoading();
        const userStr = localStorage.getItem("user");
        const user = userStr ? JSON.parse(userStr) : null;
        const modBy = user?.userName || "";
        const segmentId = parseInt(segment.id, 10);
        if (isNaN(segmentId)) {
          hideLoading();
          setIsEditing(false);
          return;
        }
        await WorkOrderQueueAPI.updateSegmentEstimatedHours(
          segmentId,
          hoursToSave,
          modBy,
          new Date().toISOString(),
          segment.rowVersion,
        );
        onUpdateHours?.(segment.id, hoursToSave);
      } catch (error) {
        if (error instanceof ConcurrencyError) {
          cacheSegmentEdit(segment.id, hoursToSave);
          onConcurrencyError?.(
            "The segment has been updated by some other user, please reload the page.",
          );
        } else {
          enqueueSnackbar("Failed to update segment hours.", { variant: "error" });
        }
      } finally {
        hideLoading();
      }
    }
    setIsEditing(false);
  };

  const handleCancelEditing = () => {
    setEditHours(segment.estimatedHours);
    setEditDisplayString(formatHours(segment.estimatedHours));
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveHours();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancelEditing();
    }
  };

  const StatusIcon =
    schedulingStatus === "completed"
      ? CheckCircleIcon
      : schedulingStatus === "archived"
        ? ArchiveIcon
        : schedulingStatus === "past_scheduled"
          ? ScheduleIcon
          : schedulingStatus === "scheduled" ||
              schedulingStatus === "partially_scheduled"
            ? EventIcon
            : null;

  const dragTooltip = !canDrag
    ? schedulingStatus === "completed"
      ? "Completed segments cannot be rescheduled"
      : schedulingStatus === "scheduled"
        ? "Future scheduled segments are managed on the calendar"
        : "This segment cannot be dragged"
    : schedulingStatus === "partially_scheduled"
      ? `Drag to schedule remaining ${formatHours(dragHours)}h`
      : schedulingStatus === "past_scheduled"
        ? "Expand to drag individual past-due tasks"
        : undefined;

  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
        {segment.name}
      </Typography>
      <Typography variant="caption" sx={{ display: "block" }}>
        Status: {statusVisual.label}
      </Typography>
      {customerName && (
        <Typography variant="caption" sx={{ display: "block" }}>
          Customer: {customerName}
        </Typography>
      )}
      <Typography variant="caption" sx={{ display: "block" }}>
        Estimated: {formatHours(segment.estimatedHours)}h
      </Typography>
      {(segment.scheduledHours ?? 0) > 0 && (
        <Typography variant="caption" sx={{ display: "block" }}>
          Scheduled: {formatHours(segment.scheduledHours ?? 0)}h
        </Typography>
      )}
      {(segment.remainingHours ?? 0) > 0 && (
        <Typography variant="caption" sx={{ display: "block" }}>
          Remaining: {formatHours(segment.remainingHours ?? 0)}h
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip arrow placement="top" title={dragTooltip ?? tooltipContent}>
      <Box>
        <Card
          draggable={canDrag && dragHours > 0 && !isEditing}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onClick={handleCardClick}
          elevation={isEditing ? 4 : isDragging ? 8 : 1}
          sx={{
            p: 1.25,
            bgcolor: statusVisual.backgroundColor || "background.default",
            cursor: canDrag && dragHours > 0 ? "grab" : "not-allowed",
            "&:active": {
              cursor: canDrag && dragHours > 0 ? "grabbing" : "not-allowed",
            },
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            border: isBroken ? "2px dashed" : "1px solid",
            borderColor: isBroken ? "error.light" : "divider",
            borderRadius: 2,
            borderLeft: `3px solid ${
              statusVisual.borderColor !== "transparent"
                ? statusVisual.borderColor
                : getBorderLeftColor(woStatusId, workOrderPriority)
            }`,
            opacity: statusVisual.opacity * (isDragging ? 0.5 : 1),
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 0.5,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                {StatusIcon && (
                  <StatusIcon sx={{ fontSize: 14, color: statusVisual.borderColor }} />
                )}
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: "0.7rem", color: "text.primary" }}
                >
                  {segment.name}
                </Typography>
              </Box>

              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                {schedulingStatus !== "unscheduled" && (
                  <Chip
                    size="small"
                    label={statusVisual.label}
                    color={statusVisual.chipColor}
                    variant="outlined"
                    sx={{ height: 20, fontSize: "0.65rem" }}
                  />
                )}
                {showTaskList && (
                  <Chip
                    size="small"
                    label={`${tasks.length} task${tasks.length === 1 ? "" : "s"}`}
                    sx={{ height: 20, fontSize: "0.65rem" }}
                  />
                )}
              </Stack>

              {schedulingStatus === "partially_scheduled" && (
                <Box sx={{ mb: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={scheduledProgress}
                    sx={{ height: 6, borderRadius: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatHours(segment.scheduledHours ?? 0)}h scheduled •{" "}
                    {formatHours(segment.remainingHours ?? 0)}h remaining
                  </Typography>
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor:
                    schedulingStatus === "unscheduled" ||
                    schedulingStatus === "partially_scheduled"
                      ? "pointer"
                      : "default",
                }}
                onMouseEnter={() =>
                  (schedulingStatus === "unscheduled" ||
                    schedulingStatus === "partially_scheduled") &&
                  setIsHoveringHours(true)
                }
                onMouseLeave={() => setIsHoveringHours(false)}
                onClick={handleStartEditing}
              >
                {isEditing ? (
                  <TextField
                    inputRef={inputRef}
                    size="small"
                    type="text"
                    value={editDisplayString}
                    onChange={(e) => setEditDisplayString(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleCancelEditing}
                    onClick={(e) => e.stopPropagation()}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveHours();
                              }}
                            >
                              <CheckIcon fontSize="small" color="success" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelEditing();
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: 140 }}
                  />
                ) : (
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "primary.main" }}>
                    {formatHours(
                      schedulingStatus === "partially_scheduled"
                        ? segment.remainingHours ?? 0
                        : segment.estimatedHours,
                    )}
                    h{" "}
                    {schedulingStatus === "partially_scheduled" ? "remaining" : "estimated"}
                    {isHoveringHours &&
                      (schedulingStatus === "unscheduled" ||
                        schedulingStatus === "partially_scheduled") && (
                        <EditIcon sx={{ fontSize: 12, opacity: 0.7 }} />
                      )}
                  </Typography>
                )}
              </Box>
            </Box>

            {showTaskList && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded((prev) => !prev);
                }}
              >
                {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            )}
          </Box>

          {showTaskList && !expanded && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {getSegmentTaskSummary(tasks)}
            </Typography>
          )}

          {isBroken && (
            <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 1 }}>
              Broken down • Cannot drag directly
            </Typography>
          )}
        </Card>

        <Collapse in={expanded && showTaskList}>
          <Stack spacing={0.75} sx={{ mt: 0.75, pl: 1 }}>
            {tasks.map((task) => (
              <SegmentTaskRow
                key={task.segmentTaskId}
                task={task}
                segment={segment}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                workOrderNumber={workOrderNumber}
                invoiceNumber={invoiceNumber}
                customerName={customerName}
                customerNumber={customerNumber}
                woStatusId={woStatusId}
                woDescription={woDescription}
                woPriorityId={woPriorityId}
              />
            ))}
          </Stack>
        </Collapse>
      </Box>
    </Tooltip>
  );
}

export const SegmentItem = memo(
  SegmentItemComponent,
  (prevProps, nextProps) =>
    prevProps.segment.id === nextProps.segment.id &&
    prevProps.segment.hours === nextProps.segment.hours &&
    prevProps.segment.estimatedHours === nextProps.segment.estimatedHours &&
    prevProps.segment.schedulingStatus === nextProps.segment.schedulingStatus &&
    prevProps.segment.scheduledHours === nextProps.segment.scheduledHours &&
    prevProps.segment.remainingHours === nextProps.segment.remainingHours &&
    prevProps.segment.taskCount === nextProps.segment.taskCount &&
    JSON.stringify(prevProps.segment.tasks ?? []) ===
      JSON.stringify(nextProps.segment.tasks ?? []) &&
    prevProps.isBroken === nextProps.isBroken &&
    prevProps.reserved === nextProps.reserved &&
    prevProps.workOrderPriority === nextProps.workOrderPriority &&
    prevProps.woStatusId === nextProps.woStatusId,
);
